package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import org.jetbrains.compose.web.css.Position
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.content
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

val LChipStyle = CssStyle.base {
    Modifier
        .styleModifier { property("display", "inline-flex") }
        .alignItems(AlignItems.Center)
        .gap(Space.XS)
        .height(32.px)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .color(colorMode.toSitePalette().textSecondary)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(Radius.Pill)
        .padding(leftRight = Space.SM)
        .fontSize(TypeScale.Small)
        .fontWeight(500)
        .lineHeight(1)
        .whiteSpace(WhiteSpace.NoWrap)
        .transitionColors()
}

val LChipRemoveStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .styleModifier { property("display", "inline-flex") }
            .alignItems(AlignItems.Center)
            .justifyContent(JustifyContent.Center)
            .width(24.px)
            .height(24.px)
            .backgroundColor(Colors.Transparent)
            .color(colorMode.toSitePalette().textMuted)
            .borderRadius(Radius.Pill)
            .padding(Space.XS)
            .cursor(Cursor.Pointer)
            .styleModifier { property("border", "0") }
            // The helper, not a hand-written string. The two properties this used to name are both
            // in `Motion.ColorProperties`, so the rendering is unchanged — what changes is that a
            // future edit to that array now reaches this control instead of stopping at it.
            .transitionColors()
    }
    /**
     * A 40px pointer target centred on a 24px control.
     *
     * The paint is 24 because a remove affordance inside a chip has to read as subordinate to the
     * chip — a 40px circle beside a 28px label is a different component. The *target* still has to be
     * 40: the pen states the hit floor numerically and does not exempt small controls, and this is the
     * one a fingertip misses most often, because it sits at the edge of a chip whose whole surface is
     * not clickable.
     *
     * Same mechanism as `LIconButtonHitAreaStyle` and the short-button strip in `LButton.kt`: an
     * absolutely-positioned pseudo-element, so nothing about the layout or the paint moves.
     *
     * 40 exactly, never more. Chips sit in a wrap-gapped row at [Space.SM] — 8px — so a wider overlay
     * would swallow clicks meant for the next chip's remove button, which is a worse defect than the
     * one being fixed.
     */
    cssRule("::after") {
        Modifier
            .content("")
            .position(Position.Absolute)
            .styleModifier {
                property("top", "50%")
                property("left", "50%")
                property("width", "${HIT_AREA_FLOOR_PX}px")
                property("height", "${HIT_AREA_FLOOR_PX}px")
                property("transform", "translate(-50%, -50%)")
            }
    }
    hover {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

@Composable
fun LChip(
    text: String,
    modifier: Modifier = Modifier,
    onRemove: (() -> Unit)? = null,
    removeLabel: String = tf("ds.ds.lchip.remove_text", "Remove {text}", "text" to text),
) {
    Span(LChipStyle.toModifier().then(modifier).toAttrs()) {
        Text(text)
        if (onRemove != null) {
            Button(
                LChipRemoveStyle.toModifier().toAttrs {
                    type(ButtonType.Button)
                    attr("aria-label", removeLabel)
                    onClick { event ->
                        event.stopPropagation()
                        onRemove()
                    }
                },
            ) {
                LCloseIcon(Modifier.iconSize(IconSize.SM))
            }
        }
    }
}

/**
 * A filter the reader turns on and off, with the count it would leave behind.
 *
 * `<button aria-pressed>`, not a checkbox and not a `role="switch"`: this is a control that changes
 * what a list shows the moment it is pressed, which is the one case `aria-pressed` names exactly.
 * A checkbox would promise a form that is submitted later, and there is none.
 *
 * The selected ground is `accentSubtle` with an `accent` hairline — the same pairing a selected
 * table row uses — so selection is carried by two channels and survives greyscale. `count` is drawn
 * in `tabularNumbers()` so a row of chips does not jitter sideways as counts refresh underneath it.
 *
 * 40px tall rather than the 32 of [LChip]. A plain chip is a label with a remove affordance inside
 * it; this one *is* the affordance, and the hit floor has no exemption for small controls.
 */
val LToggleChipStyle = CssStyle {
    base {
        Modifier
            .styleModifier { property("display", "inline-flex") }
            .alignItems(AlignItems.Center)
            .gap(Space.XS)
            .minHeight(HIT_AREA_FLOOR_PX.px)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Pill)
            .padding(leftRight = Space.MD)
            .fontSize(TypeScale.Small)
            .fontWeight(500)
            .lineHeight(1)
            .whiteSpace(WhiteSpace.NoWrap)
            .cursor(Cursor.Pointer)
            .transitionColors()
    }
    cssRule(":not(:disabled):hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken)
    }
    cssRule("[aria-pressed=\"true\"]") {
        Modifier
            .backgroundColor(colorMode.toSitePalette().accentSubtle)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().accent)
    }
    cssRule("[data-disabled=\"true\"]") { Modifier.disabledSurface() }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

val LToggleChipCountStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Small)
        .tabularNumbers()
}

@Composable
fun LToggleChip(
    text: String,
    selected: Boolean,
    onSelectedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    count: Int? = null,
    icon: (@Composable () -> Unit)? = null,
    enabled: Boolean = true,
) {
    Button(
        LToggleChipStyle.toModifier().then(modifier).toAttrs {
            type(ButtonType.Button)
            // The state, said out loud. `data-selected` is for the test and the stylesheet;
            // `aria-pressed` is the only channel a reader who cannot see the accent fill has, and
            // it is what makes a `<button>` legal as a toggle at all.
            attr("aria-pressed", selected.toString())
            attr("data-selected", selected.toString())
            attr("data-disabled", (!enabled).toString())
            if (!enabled) disabled()
            onClick { onSelectedChange(!selected) }
        },
    ) {
        if (icon != null) {
            Span(Modifier.iconSize(IconSize.SM).styleModifier { property("display", "inline-flex") }.toAttrs()) {
                icon()
            }
        }
        Text(text)
        if (count != null) {
            Span(
                LToggleChipCountStyle.toModifier()
                    .color(
                        if (selected) {
                            ColorMode.current.toSitePalette().textSecondary
                        } else {
                            ColorMode.current.toSitePalette().textMuted
                        },
                    )
                    .toAttrs { attr("data-toggle-chip-count", "") },
            ) {
                Text(formatCount(count))
            }
        }
    }
}
